import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sofa, Sun, Layers, Activity, ChevronRight, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import RefreshBar from "@/components/RefreshBar";
import { InlineLoader } from "@/components/Spinner";
import { dateLabel, initials } from "@/lib/status";

const CARDS = [
  { key: "free", label: "Free Baldachins", icon: Sun, accent: "#A8A29E", valueKey: "free" },
  { key: "taken", label: "Taken", icon: Sofa, accent: "#0284C7", valueKey: "taken" },
  { key: "sold_multiple", label: "Sold Multiple Times", icon: Layers, accent: "#EA580C", valueKey: "sold_multiple" },
  { key: "actions", label: "Total Sale Actions", icon: Activity, accent: "#1C1917", valueKey: "total_actions" },
];

const ACTION_COLOR = {
  sold: "bg-sky-50 text-sky-700",
  sold_again: "bg-orange-50 text-orange-700",
  reopened: "bg-stone-100 text-stone-700",
  edited: "bg-violet-50 text-violet-700",
  deleted: "bg-red-50 text-red-700",
  layout_updated: "bg-stone-100 text-stone-600",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback((spin = false) => {
    if (spin) setLoading(true);
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(true); }, [load]);

  if (loading && !data) return <InlineLoader label="Loading dashboard…" />;

  return (
    <div className="space-y-6 bb-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Owner Console</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-sm text-stone-500">{dateLabel(data?.date)}</p>
        </div>
        <RefreshBar lastUpdated={data?.last_updated} onRefresh={() => load(true)} loading={loading} testid="dashboard-refresh" />
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {CARDS.map((c) => (
          <button
            key={c.key}
            data-testid={`dashboard-card-${c.key}`}
            onClick={() => navigate(`/admin/stats/${c.key}`)}
            className="group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${c.accent}1a`, color: c.accent }}>
                <c.icon className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <ChevronRight className="h-4 w-4 text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-stone-500" />
            </div>
            <div>
              <p className="font-heading text-4xl font-black tracking-tighter" style={{ color: c.accent }}>
                {data?.summary?.[c.valueKey] ?? 0}
              </p>
              <p className="text-sm font-semibold text-stone-500">{c.label}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="border-b border-stone-100 px-5 py-4">
            <h2 className="font-heading text-lg font-bold">Latest worker actions</h2>
          </div>
          {data?.feed?.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center text-stone-400" data-testid="feed-empty">
              <Inbox className="h-8 w-8" strokeWidth={1.8} />
              <p className="text-sm font-medium">No actions yet today</p>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="dashboard-feed">
              {data?.feed?.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-900 text-xs font-bold text-white">
                    {initials(f.user_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {f.user_name} <span className="font-normal text-stone-400">·</span>{" "}
                      <span className="font-heading font-bold">#{f.baldachin_number}</span>
                    </p>
                    <p className="text-xs text-stone-400">{f.user_role}</p>
                  </div>
                  <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${ACTION_COLOR[f.action_type] || "bg-stone-100"}`}>
                    {f.action_label}
                  </span>
                  <span className="w-12 text-right text-xs font-medium text-stone-500">{f.time}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-heading text-lg font-bold">Worker activity</h2>
          {data?.worker_activity?.length === 0 ? (
            <p className="text-sm text-stone-400">No workers yet.</p>
          ) : (
            <ul className="space-y-3" data-testid="worker-activity">
              {data?.worker_activity?.map((w) => {
                const max = Math.max(1, ...data.worker_activity.map((x) => x.actions));
                return (
                  <li key={w.id}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 font-semibold">
                        {w.name}
                        {!w.active && <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-bold text-stone-500">INACTIVE</span>}
                      </span>
                      <span className="font-heading font-bold">{w.actions}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${(w.actions / max) * 100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
